import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';

/**
 * What a list control is actually asking for, told apart from it describing itself.
 *
 * `DataTable` reports its paginator and its sort as it initialises, with the `first` and
 * `presortOrder` the caller handed it — so every list in this section answered its own opening
 * bindings with a second read of the same page, on top of the one the view had already made on
 * arrival. Neither store could see it: `onPageChange` compared nothing at all, and `setSortOrder`
 * compared PrimeVue's `-1`/`1` against its own `'ASC'`/`'DESC'`, which are never equal.
 *
 * Both rules return the change or `undefined`, rather than a boolean beside the values, so a caller
 * cannot act on a change it has just been told is not one. Pure and shared, because three stores
 * had grown the same two handlers and would have grown the same two fixes.
 */

/** Which direction a list is sorted in, in the spelling the API takes. */
export type SortDirection = 'ASC' | 'DESC';

/**
 * The page and size a paginator event asks for, one-based, or `undefined` when it names the page
 * the list is already showing.
 */
export const pageChangeFrom = (
  event: PaginatorData,
  currentPage: number,
  pageLimit: number
): { page: number; limit: number } | undefined => {
  const page = event.page + 1;
  if (page === currentPage && event.rows === pageLimit) {
    return undefined;
  }
  return { page, limit: event.rows };
};

/**
 * The direction a sort-order event asks for, or `undefined` when it is the direction already in
 * force — or no direction at all, which is `removableSort` clearing the column rather than
 * reversing it.
 *
 * Nothing is lost by reading a cleared sort as no change. `removableSort` clears the *field* in the
 * same breath, and a store's `setSort` refetches on that; the direction left behind never reaches
 * the API, which appends `sortOrder` only inside `if (sortBy)`.
 */
export const sortDirectionFrom = (
  order: number | null | undefined,
  current?: string
): SortDirection | undefined => {
  if (order === undefined || order === null) {
    return undefined;
  }
  // Ascending is the only value the table spells positively; everything else it sends means the
  // other way round.
  const direction: SortDirection = order === 1 ? 'ASC' : 'DESC';
  return direction === current ? undefined : direction;
};
